import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Quest, QuestType, QuestDifficulty } from '../types';
import { getXPRewardForDifficulty, getCurrencyRewardForDifficulty, getStatRewardsForDifficulty } from '../utils/levelSystem';
import { useLanguage } from '../contexts/LanguageContext';

interface QuestEditModalProps {
  quest: Quest;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (questId: string, updates: Partial<Quest>) => void;
  onDelete: (questId: string, silent?: boolean, deleteSeries?: boolean) => void;
  onComplete: (questId: string) => void;
  // When opened from calendar, show only title, description, and sub-tasks
  calendarCompact?: boolean;
}

const QuestEditModal: React.FC<QuestEditModalProps> = ({
  quest,
  isOpen,
  onClose,
  onUpdate,
  onDelete,
  onComplete,
  calendarCompact = false,
}) => {
  const { t } = useLanguage();
  const [isEditing, setIsEditing] = useState(calendarCompact ? true : false);
  const [title, setTitle] = useState(quest.title);
  const [description, setDescription] = useState(quest.description);
  const [type, setType] = useState<QuestType>(quest.type);
  const [difficulty, setDifficulty] = useState<QuestDifficulty>(quest.difficulty);
  const [scheduledFor, setScheduledFor] = useState(quest.scheduledFor || '');
  const [scheduledTime, setScheduledTime] = useState(quest.scheduledTime);
  const [allDay, setAllDay] = useState(quest.allDay || false);
  const [customColor, setCustomColor] = useState<string | undefined>(quest.customColor);
  const [resetDaily, setResetDaily] = useState(quest.resetDaily || false);
  const [subTasks, setSubTasks] = useState(quest.subTasks || []);
  const [newSubTaskText, setNewSubTaskText] = useState('');

  // Color presets inspired by Solo Leveling + Google Calendar
  const colorPresets = [
    { name: t.questEdit.colors.default, value: undefined, gradient: 'from-gray-600 to-gray-700', border: 'border-gray-500' },
    { name: t.questEdit.colors.shadowBlue, value: 'blue', gradient: 'from-blue-600 to-blue-700', border: 'border-blue-500' },
    { name: t.questEdit.colors.monarchPurple, value: 'purple', gradient: 'from-purple-600 to-purple-700', border: 'border-purple-500' },
    { name: t.questEdit.colors.emeraldGreen, value: 'green', gradient: 'from-green-600 to-green-700', border: 'border-green-500' },
    { name: t.questEdit.colors.bloodRed, value: 'red', gradient: 'from-red-600 to-red-700', border: 'border-red-500' },
    { name: t.questEdit.colors.goldMonarch, value: 'yellow', gradient: 'from-yellow-600 to-orange-600', border: 'border-yellow-500' },
    { name: t.questEdit.colors.iceCrystal, value: 'cyan', gradient: 'from-cyan-600 to-cyan-700', border: 'border-cyan-500' },
    { name: t.questEdit.colors.pinkMagic, value: 'pink', gradient: 'from-pink-600 to-pink-700', border: 'border-pink-500' },
    { name: t.questEdit.colors.darkVoid, value: 'gray', gradient: 'from-gray-700 to-gray-800', border: 'border-gray-600' },
  ];

  const typeColors: Record<QuestType, string> = {
    daily: 'from-blue-600 to-blue-700 border-blue-500 text-blue-300',
    main: 'from-purple-600 to-purple-700 border-purple-500 text-purple-300',
    instant: 'from-green-600 to-green-700 border-green-500 text-green-300',
    boss: 'from-red-600 to-red-700 border-red-500 text-red-300',
  };

  const difficultyColors: Record<QuestDifficulty, string> = {
    easy: 'from-green-500 to-emerald-600 border-green-500',
    normal: 'from-blue-500 to-cyan-600 border-blue-500',
    hard: 'from-orange-500 to-red-600 border-orange-500',
    boss: 'from-red-600 to-purple-700 border-red-500',
  };

  const typeIcons: Record<QuestType, string> = {
    daily: '📋',
    main: '⚔️',
    instant: '⚡',
    boss: '🐉',
  };

  const getTodayDateString = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const handleSave = () => {
    // Check if difficulty changed - if so, recalculate rewards
    const updates: Partial<Quest> = {
      title,
      description,
      type,
      difficulty,
      ...(calendarCompact ? {} : {
        scheduledFor: scheduledFor || undefined,
        scheduledTime: (!allDay && scheduledFor && scheduledTime) ? scheduledTime : undefined,
        allDay,
        resetDaily,
      }),
      customColor,
      subTasks: subTasks && subTasks.length > 0 ? subTasks : undefined,
    };

    // If difficulty changed, update rewards
    if (difficulty !== quest.difficulty) {
      updates.xpReward = getXPRewardForDifficulty(difficulty);
      updates.currencyReward = getCurrencyRewardForDifficulty(difficulty);
      updates.statRewards = getStatRewardsForDifficulty(difficulty);
    }

    onUpdate(quest.id, updates);
    setIsEditing(false);
  };

  const addSubTask = () => {
    const text = newSubTaskText.trim();
    if (!text) return;
    setSubTasks(prev => [...prev, { id: Date.now().toString(), text, completed: false }]);
    setNewSubTaskText('');
  };

  const removeSubTask = (id: string) => {
    setSubTasks(prev => prev.filter(st => st.id !== id));
  };

  const handleCancel = () => {
    // Reset to original values
    setTitle(quest.title);
    setDescription(quest.description);
    setType(quest.type);
    setDifficulty(quest.difficulty);
    setScheduledFor(quest.scheduledFor || '');
    setScheduledTime(quest.scheduledTime);
    setAllDay(quest.allDay || false);
    setCustomColor(quest.customColor);
    setResetDaily(quest.resetDaily || false);
    setSubTasks(quest.subTasks || []);
    setNewSubTaskText('');
    setIsEditing(false);
  };

  const handleTimeChange = (field: 'start' | 'end', value: string) => {
    setScheduledTime(prev => ({
      start: field === 'start' ? value : (prev?.start || '09:00'),
      end: field === 'end' ? value : (prev?.end || '10:00'),
    }));
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 backdrop-blur-xl flex items-center justify-center z-[100] p-4 modal-backdrop animate-fade-in"
      onClick={onClose}
    >
      <div 
        className="relative bg-gradient-to-b from-slate-800/50 via-slate-900/50 to-slate-950/50 backdrop-blur-md border-t-2 border-l-2 border-purple-500/40 border-r-2 border-b-2 border-r-purple-700/60 border-b-purple-700/60 rounded-3xl shadow-2xl max-w-2xl w-full max-h-[75vh] sm:max-h-[85vh] overflow-y-auto animate-scale-in"
          onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
        }}
      >
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-b from-slate-800/60 via-slate-900/50 to-slate-950/60 backdrop-blur-sm border-b border-b-slate-500/30 p-3 sm:p-4 flex items-center justify-between z-10"
            style={{
              boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="text-xl sm:text-3xl">{typeIcons[quest.type]}</div>
              <div>
                <h2 className="font-orbitron text-base sm:text-2xl font-bold text-system-blue uppercase tracking-wider mb-0.5 sm:mb-1">
                  ［ {t.questEdit.questDetails} ］
                </h2>
                <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                  <span className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-orbitron font-bold uppercase bg-gradient-to-r ${typeColors[quest.type]}`}>
                    {t.quests[quest.type as keyof typeof t.quests]}
                  </span>
                  <span className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-orbitron font-bold uppercase bg-gradient-to-r ${difficultyColors[quest.difficulty]}`}>
                    {quest.difficulty}
                  </span>
                  {quest.status === 'completed' && (
                    <span className="px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-orbitron font-bold uppercase bg-green-600 text-white">
                      ✓ {t.questEdit.completed}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-xl leading-none transition-all bg-gradient-to-b from-slate-700/50 to-slate-800/50 rounded-full p-2 border-t-2 border-l-2 border-slate-600/40 border-r-2 border-b-2 border-slate-700/60"
              style={{
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
              }}
              title={t.questEdit.close}
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 relative">
            {/* Title & Description */}
            <div className="space-y-2.5 sm:space-y-3">
              <div>
                <label className="block text-xs sm:text-sm font-orbitron font-bold text-gray-300 uppercase tracking-wider mb-1.5 sm:mb-2">
                  {t.questEdit.questTitle}
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 focus:border-blue-500 rounded-xl px-3 py-2 text-white font-rajdhani text-sm outline-none transition-all"
                    style={{
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                    }}
                  />
                ) : (
                  <div className="text-base font-bold text-white font-rajdhani px-2 py-1">
                    {quest.title}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-orbitron font-bold text-gray-300 uppercase tracking-wider mb-1.5 sm:mb-2">
                  {t.quests.description}
                </label>
                {isEditing ? (
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="w-full bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 focus:border-blue-500 rounded-xl px-3 py-2 text-white font-rajdhani outline-none transition-all resize-none text-sm"
                    style={{
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                    }}
                  />
                ) : (
                  <div className="text-gray-300 font-rajdhani text-sm px-2 py-1">
                    {quest.description || t.questEdit.noDescription}
                  </div>
                )}
              </div>
            </div>

            {/* Quest Type & Difficulty (always shown in compact mode) */}
            {isEditing && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                <div>
                  <label className="block text-xs sm:text-sm font-orbitron font-bold text-gray-300 uppercase tracking-wider mb-2 sm:mb-3">
                    {t.quests.questType}
                  </label>
                  <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                    {(['daily', 'main', 'instant', 'boss'] as QuestType[]).map((questType) => (
                      <button
                        key={questType}
                        onClick={() => setType(questType)}
                        className={`px-2 py-2 rounded-xl border-t-2 border-l-2 border-r-2 border-b-2 font-orbitron font-bold text-[10px] uppercase transition-all relative ${
                          type === questType
                            ? `bg-gradient-to-b ${typeColors[questType].replace('to-r', 'to-b')} scale-105 shadow-[0_0_15px_rgba(59,130,246,0.5)]`
                            : 'bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-slate-500/40 border-r-slate-700/60 border-b-slate-700/60 text-gray-400 hover:border-blue-500/50 hover:scale-105'
                        }`}
                        style={type === questType ? {
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                        } : {
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                        }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-xl"></div>
                        <span className="relative z-10 text-xs">{typeIcons[questType]}</span> 
                        <span className="relative z-10 text-[10px]">{t.quests[questType as keyof typeof t.quests]}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-orbitron font-bold text-gray-300 uppercase tracking-wider mb-2 sm:mb-3">
                    {t.quests.difficulty}
                  </label>
                  <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                    {(['easy', 'normal', 'hard', 'boss'] as QuestDifficulty[]).map((d) => (
                      <button
                        key={d}
                        onClick={() => setDifficulty(d)}
                        className={`px-2 py-2 rounded-xl border-t-2 border-l-2 border-r-2 border-b-2 font-orbitron font-bold text-[10px] uppercase transition-all relative ${
                          difficulty === d
                            ? `bg-gradient-to-b ${difficultyColors[d].replace('to-r', 'to-b').replace('from-', 'from-')} scale-105 text-white shadow-[0_0_15px_rgba(59,130,246,0.5)]`
                            : 'bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-slate-500/40 border-r-slate-700/60 border-b-slate-700/60 text-gray-400 hover:border-blue-500/50 hover:scale-105'
                        }`}
                        style={difficulty === d ? {
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                        } : {
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                        }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-xl"></div>
                        <span className="relative z-10 text-[10px]">{d}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Daily Reset Toggle (hidden in compact mode) */}
            {isEditing && !calendarCompact && (
              <div className="bg-gradient-to-b from-orange-950/40 via-slate-950/30 to-orange-950/40 backdrop-blur-sm border-t-2 border-l-2 border-orange-500/40 border-r-2 border-b-2 border-r-orange-700/60 border-b-orange-700/60 rounded-2xl p-3 sm:p-4"
                style={{
                  boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                }}
              >
                <div className="flex items-center gap-2 mb-3 sm:mb-4">
                  <span className="text-xl">🔄</span>
                  <label className="text-sm font-orbitron font-bold text-gray-300 uppercase tracking-wider">
                    {t.questEdit.dailyReset}
                  </label>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-xs sm:text-sm font-rajdhani text-gray-300 mb-1">
                      {t.questEdit.resetDaily}
                    </div>
                    <div className="text-[11px] sm:text-xs text-gray-500 font-rajdhani">
                      {resetDaily 
                        ? t.questEdit.resetDailyDescription
                        : t.questEdit.oneTimeDescription
                      }
                    </div>
                  </div>
                  
                  <div className="ml-4">
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={resetDaily}
                          onChange={(e) => setResetDaily(e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`w-10 h-5 sm:w-12 sm:h-6 rounded-full transition-all ${resetDaily ? 'bg-orange-600' : 'bg-gray-600'}`}>
                          <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${resetDaily ? 'translate-x-5 sm:translate-x-6' : ''}`} />
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
                
                <div className="mt-2.5 sm:mt-3 text-[11px] sm:text-xs text-gray-500 font-rajdhani italic text-center">
                  💡 {resetDaily 
                    ? t.questEdit.dailyResetTooltip
                    : t.questEdit.oneTimeTooltip
                  }
                </div>
              </div>
            )}

            {/* Calendar Color Picker (also shown in compact mode) */}
            {isEditing && (
              <div className="bg-gradient-to-b from-purple-950/40 via-slate-950/30 to-purple-950/40 backdrop-blur-sm border-t-2 border-l-2 border-purple-500/40 border-r-2 border-b-2 border-r-purple-700/60 border-b-purple-700/60 rounded-2xl p-3 sm:p-4"
                style={{
                  boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                }}
              >
                <div className="flex items-center gap-2 mb-2.5 sm:mb-3">
                  <span className="text-2xl">🎨</span>
                  <label className="text-sm font-orbitron font-bold text-gray-300 uppercase tracking-wider">
                    {t.questEdit.calendarColor}
                  </label>
                </div>
                <div className="grid grid-cols-3 gap-2.5 sm:gap-3">
                  {colorPresets.map((color) => (
                    <button
                      key={color.value || 'default'}
                      onClick={() => setCustomColor(color.value)}
                      className={`relative px-4 py-3 rounded-lg border-2 font-rajdhani font-bold text-xs uppercase transition-all group ${
                        customColor === color.value
                          ? `bg-gradient-to-r ${color.gradient} ${color.border} shadow-[0_0_15px_rgba(59,130,246,0.6)] scale-105 text-white`
                          : `bg-system-darker ${color.border} text-gray-400 hover:scale-105`
                      }`}
                    >
                      {customColor === color.value && (
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-system-blue rounded-full flex items-center justify-center text-white text-xs font-bold shadow-neon-blue">
                          ✓
                        </div>
                      )}
                      <div className="flex flex-col items-center gap-1">
                        <div className={`w-8 h-8 rounded-full bg-gradient-to-r ${color.gradient} border-2 ${color.border} shadow-lg`}></div>
                        <span className="text-[10px]">{color.name}</span>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="mt-2.5 sm:mt-3 text-xs text-gray-500 font-rajdhani italic text-center">
                  💡 {t.questEdit.colorTooltip}
                </div>
              </div>
            )}

            {/* Schedule Date & Time (hidden in compact mode) */}
            {isEditing && !calendarCompact && (
              <div className="bg-gradient-to-b from-purple-950/40 via-slate-950/30 to-purple-950/40 backdrop-blur-sm border-t-2 border-l-2 border-purple-500/40 border-r-2 border-b-2 border-r-purple-700/60 border-b-purple-700/60 rounded-2xl p-3 sm:p-4"
                style={{
                  boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                }}
              >
                <div className="flex items-center gap-2 mb-3 sm:mb-4">
                  <span className="text-xl">📅</span>
                  <label className="text-sm font-orbitron font-bold text-gray-300 uppercase tracking-wider">
                    {t.quests.schedule}
                  </label>
                </div>

                {/* Date Picker */}
                <div className="mb-3">
                  <label className="block text-xs font-rajdhani text-gray-400 uppercase mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    value={scheduledFor}
                    onChange={(e) => setScheduledFor(e.target.value)}
                    min={getTodayDateString()}
                    className="w-full bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 focus:border-purple-500 rounded-2xl px-3 py-2 sm:px-4 sm:py-3 text-white font-rajdhani outline-none transition-all cursor-pointer"
                    style={{ 
                      colorScheme: 'dark',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                    }}
                  />
                </div>

                {/* All Day Toggle */}
                {scheduledFor && (
                  <div className="mb-3">
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={allDay}
                          onChange={(e) => setAllDay(e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`w-10 h-5 sm:w-12 sm:h-6 rounded-full transition-all ${allDay ? 'bg-purple-600' : 'bg-gray-600'}`}>
                          <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${allDay ? 'translate-x-5 sm:translate-x-6' : ''}`} />
                        </div>
                      </div>
                      <span className="text-xs sm:text-sm font-rajdhani text-gray-300 group-hover:text-white transition-colors">
                        All Day Quest
                      </span>
                    </label>
                  </div>
                )}

                {/* Time Pickers */}
                {scheduledFor && !allDay && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    <div>
                      <label className="block text-xs font-rajdhani text-gray-400 uppercase mb-1">
                        ⏰ Start Time
                      </label>
                      <input
                        type="time"
                        value={scheduledTime?.start || '09:00'}
                        onChange={(e) => handleTimeChange('start', e.target.value)}
                        className="w-full bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 focus:border-purple-500 rounded-2xl px-3 py-2 sm:px-4 sm:py-3 text-white font-rajdhani outline-none transition-all cursor-pointer"
                        style={{ 
                          colorScheme: 'dark',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-rajdhani text-gray-400 uppercase mb-1">
                        ⏰ End Time
                      </label>
                      <input
                        type="time"
                        value={scheduledTime?.end || '10:00'}
                        onChange={(e) => handleTimeChange('end', e.target.value)}
                        className="w-full bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 focus:border-purple-500 rounded-2xl px-3 py-2 sm:px-4 sm:py-3 text-white font-rajdhani outline-none transition-all cursor-pointer"
                        style={{ 
                          colorScheme: 'dark',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Display scheduled info when not editing */}
            {!isEditing && !calendarCompact && (quest.scheduledFor || quest.scheduledTime) && (
              <div className="bg-gradient-to-b from-purple-950/40 via-slate-950/30 to-purple-950/40 backdrop-blur-sm border-t-2 border-l-2 border-purple-500/40 border-r-2 border-b-2 border-r-purple-700/60 border-b-purple-700/60 rounded-2xl p-3 sm:p-4"
                style={{
                  boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                }}
              >
                <div className="flex items-center gap-2 mb-2.5 sm:mb-3">
                  <span className="text-xl">📅</span>
                  <label className="text-sm font-orbitron font-bold text-gray-300 uppercase tracking-wider">
                    {t.quests.schedule}
                  </label>
                </div>
                <div className="space-y-2">
                  {quest.scheduledFor && (
                    <div>
                      <span className="text-xs text-gray-400 font-rajdhani uppercase block mb-1">Date</span>
                      <div className="text-lg font-bold text-purple-400 font-orbitron">
                        {new Date(quest.scheduledFor).toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </div>
                    </div>
                  )}
                  {quest.allDay && (
                    <div className="text-sm text-gray-400 font-rajdhani">
                      🌞 All Day Quest
                    </div>
                  )}
                  {quest.scheduledTime && !quest.allDay && (
                    <div>
                      <span className="text-xs text-gray-400 font-rajdhani uppercase block mb-1">Time</span>
                      <div className="text-lg font-bold text-purple-400 font-orbitron">
                        {quest.scheduledTime.start} - {quest.scheduledTime.end}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Daily Reset Status */}
            {!isEditing && !calendarCompact && (
              <div className="bg-gradient-to-b from-orange-950/40 via-slate-950/30 to-orange-950/40 backdrop-blur-sm border-t-2 border-l-2 border-orange-500/40 border-r-2 border-b-2 border-r-orange-700/60 border-b-orange-700/60 rounded-2xl p-3 sm:p-4"
                style={{
                  boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                }}
              >
                <div className="flex items-center gap-2 mb-2.5 sm:mb-3">
                  <span className="text-xl">🔄</span>
                  <label className="text-sm font-orbitron font-bold text-gray-300 uppercase tracking-wider">
                    {t.questEdit.dailyReset}
                  </label>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${quest.resetDaily ? 'bg-orange-500' : 'bg-gray-500'}`}></div>
                    <span className="text-sm font-rajdhani text-gray-300">
                      {quest.resetDaily ? t.questEdit.dailyResetStatus : t.questEdit.oneTimeStatus}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 font-rajdhani">
                    {quest.resetDaily 
                      ? t.questEdit.resetDailyDescription
                      : t.questEdit.oneTimeDescription
                    }
                  </div>
                </div>
              </div>
            )}

            {/* Rewards (hidden in compact mode) */}
            {!calendarCompact && (
            <div className="bg-gradient-to-b from-amber-950/40 via-yellow-950/30 to-amber-950/40 backdrop-blur-sm border-t-2 border-l-2 border-amber-500/40 border-r-2 border-b-2 border-r-amber-700/60 border-b-amber-700/60 rounded-2xl p-3 sm:p-4"
              style={{
                boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
              }}
            >
              <div className="flex items-center justify-between mb-2.5 sm:mb-3">
                <label className="flex items-center gap-2 text-xs sm:text-sm font-orbitron font-bold text-yellow-400 uppercase tracking-wider">
                  <img 
                    src="/icons/icon-quest-instant.png" 
                    alt="Rewards"
                    className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                    style={{ filter: 'drop-shadow(0 0 4px rgba(252, 211, 77, 0.8))' }}
                  />
                  {t.questEdit.rewards}
                </label>
                {isEditing && difficulty !== quest.difficulty && (
                  <span className="flex items-center gap-1 text-[11px] sm:text-xs font-rajdhani text-green-400 animate-pulse">
                    <img 
                      src="/icons/icon-notification.png" 
                      alt="Update"
                      className="w-3.5 h-3.5 sm:w-4 sm:h-4 object-contain"
                      style={{ filter: 'drop-shadow(0 0 4px rgba(16, 185, 129, 0.6))' }}
                    />
                    {t.questEdit.willUpdateOnSave}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                <div className="text-center">
                  <div className="flex justify-center mb-2">
                    <img 
                      src="/icons/icon-xp.png" 
                      alt="XP"
                      className="w-6 h-6 sm:w-8 sm:h-8 object-contain"
                      style={{ filter: 'drop-shadow(0 0 6px rgba(59, 130, 246, 0.8))' }}
                    />
                  </div>
                  <div className={`text-2xl sm:text-3xl font-black font-orbitron text-blue-400 transition-all ${
                    isEditing && difficulty !== quest.difficulty ? 'animate-pulse scale-110' : ''
                  }`}>
                    +{isEditing && difficulty !== quest.difficulty ? getXPRewardForDifficulty(difficulty) : quest.xpReward}
                  </div>
                  <div className="text-[11px] sm:text-xs text-gray-400 font-rajdhani uppercase">{t.questEdit.xp}</div>
                </div>
                <div className="text-center">
                  <div className="flex justify-center mb-2">
                    <img 
                      src="/icons/icon-gold.png" 
                      alt="Gold"
                      className="w-6 h-6 sm:w-8 sm:h-8 object-contain"
                      style={{ filter: 'drop-shadow(0 0 6px rgba(252, 211, 77, 0.8))' }}
                    />
                  </div>
                  <div className={`text-2xl sm:text-3xl font-black font-orbitron text-yellow-400 transition-all ${
                    isEditing && difficulty !== quest.difficulty ? 'animate-pulse scale-110' : ''
                  }`}>
                    +{isEditing && difficulty !== quest.difficulty ? getCurrencyRewardForDifficulty(difficulty) : quest.currencyReward}
                  </div>
                  <div className="text-[11px] sm:text-xs text-gray-400 font-rajdhani uppercase">{t.questEdit.gold}</div>
                </div>
                <div className="text-center">
                  <div className="flex justify-center mb-2">
                    <img 
                      src="/icons/icon-statpoints.png" 
                      alt="Stats"
                      className="w-6 h-6 sm:w-8 sm:h-8 object-contain"
                      style={{ filter: 'drop-shadow(0 0 6px rgba(168, 85, 247, 0.6))' }}
                    />
                  </div>
                  <div className={`text-2xl sm:text-3xl font-black font-orbitron text-green-400 transition-all ${
                    isEditing && difficulty !== quest.difficulty ? 'animate-pulse scale-110' : ''
                  }`}>
                    +{isEditing && difficulty !== quest.difficulty 
                      ? Object.values(getStatRewardsForDifficulty(difficulty)).reduce((sum, val) => sum + val, 0)
                      : Object.values(quest.statRewards || {}).reduce((sum, val) => sum + val, 0)
                    }
                  </div>
                  <div className="text-[11px] sm:text-xs text-gray-400 font-rajdhani uppercase">{t.questEdit.stats}</div>
                </div>
              </div>
            </div>
            )}

            {/* Sub-tasks (compact mode and editing) */}
            {(calendarCompact || isEditing) && (
              <div className="bg-gradient-to-b from-green-950/40 via-slate-950/30 to-green-950/40 backdrop-blur-sm border-t-2 border-l-2 border-green-500/40 border-r-2 border-b-2 border-r-green-700/60 border-b-green-700/60 rounded-2xl p-3 sm:p-4"
                style={{
                  boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                }}
              >
                <div className="flex items-center gap-2 mb-2.5 sm:mb-3">
                  <span className="text-xl">✓</span>
                  <label className="text-sm font-orbitron font-bold text-gray-300 uppercase tracking-wider">
                    Sub-tasks
                  </label>
                </div>

                <div className="flex gap-2 mb-2.5 sm:mb-3">
                  <input
                    type="text"
                    value={newSubTaskText}
                    onChange={(e) => setNewSubTaskText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSubTask(); } }}
                    className="flex-1 bg-white/10 backdrop-blur-sm border-t-2 border-l-2 border-slate-400/40 border-r-2 border-b-2 border-r-slate-600/60 border-b-slate-600/60 rounded-xl px-3 py-2 text-white text-sm font-rajdhani placeholder:text-white/80 focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/30 transition-all"
                    placeholder="Add a sub-task"
                    style={{
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                    }}
                  />
                  <button
                    type="button"
                    onClick={addSubTask}
                    className="px-4 py-2 bg-gradient-to-b from-green-600/90 to-green-700/90 backdrop-blur-sm border-t-2 border-l-2 border-green-500/50 border-r-2 border-b-2 border-r-green-700/70 border-b-green-700/70 rounded-xl font-orbitron font-bold text-sm transition-all hover:scale-105 relative"
                    style={{
                      boxShadow: '0 4px 12px rgba(34, 197, 94, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-xl"></div>
                    <span className="relative z-10">+ Add</span>
                  </button>
                </div>

                {subTasks && subTasks.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
                    {subTasks.map((st) => (
                      <div
                        key={st.id}
                        className="flex items-center justify-between bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/30 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-xl px-3 py-2 group"
                        style={{
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                        }}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <div className="w-4 h-4 border-2 border-green-500/50 rounded"></div>
                          <span className="text-sm text-gray-300 font-rajdhani">{st.text}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeSubTask(st.id)}
                          className="text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 font-rajdhani italic">No sub-tasks added.</p>
                )}
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="sticky bottom-0 bg-gradient-to-b from-slate-800/60 via-slate-900/50 to-slate-950/60 backdrop-blur-sm border-t border-t-slate-500/30 p-3 sm:p-4"
            style={{
              boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.05)'
            }}
          >
            {/* Compact calendar footer: Complete, Delete, Save */}
            {calendarCompact ? (
              <div className="flex gap-2.5 sm:grid sm:grid-cols-3 sm:gap-3">
                {quest.status !== 'completed' && (
                  <button
                    onClick={() => {
                      onComplete(quest.id);
                      onClose();
                    }}
                    className="bg-gradient-to-b from-green-800/60 via-green-900/50 to-green-950/60 backdrop-blur-sm border-t-2 border-l-2 border-green-500/50 border-r-2 border-b-2 border-r-green-700/70 border-b-green-700/70 rounded-xl text-white h-10 flex-1 sm:flex-none sm:w-auto sm:h-auto sm:py-2 font-orbitron font-bold text-xs tracking-wide uppercase transition-all hover:scale-105 relative flex items-center justify-center"
                    style={{
                      boxShadow: '0 4px 12px rgba(34, 197, 94, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                    }}
                    title={t.questEdit.complete}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                    <span className="relative z-10 text-base">✓</span>
                    <span className="relative z-10 hidden sm:inline ml-1">{t.questEdit.complete}</span>
                  </button>
                )}
                {/* Delete button with different behavior for daily vs regular quests */}
                <button
                  onClick={() => {
                    if (quest.type === 'daily') {
                      // For daily quests, show choice dialog
                      const choice = window.confirm(
                        'Delete this daily quest?\n\n• OK: Delete only this instance (quest continues tomorrow)\n• Cancel: Choose to delete entire series'
                      );
                      if (choice) {
                        // OK: Delete only this instance (quest resets tomorrow)
                        onDelete(quest.id);
                      } else {
                        // Cancel: Ask for confirmation to delete entire series
                        if (window.confirm('⚠️ Delete ENTIRE recurring daily quest series?\n\nThis will permanently stop this daily quest from appearing again.')) {
                          onDelete(quest.id, false, true); // deleteSeries = true
                        }
                      }
                    } else {
                      // For regular quests, normal delete
                      if (window.confirm(t.questEdit.confirmDelete)) {
                        onDelete(quest.id);
                      }
                    }
                    onClose();
                  }}
                  className="bg-gradient-to-b from-red-800/60 via-red-900/50 to-red-950/60 backdrop-blur-sm border-t-2 border-l-2 border-red-500/50 border-r-2 border-b-2 border-r-red-700/70 border-b-red-700/70 rounded-xl text-white h-10 flex-1 sm:flex-none sm:w-auto sm:h-auto sm:py-2 font-orbitron font-bold text-xs tracking-wide uppercase transition-all hover:scale-105 relative flex items-center justify-center"
                  style={{
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                  }}
                  title={quest.type === 'daily' ? 'Delete daily quest (choose instance or entire series)' : t.questEdit.delete}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                  <span className="relative z-10 text-base">🗑️</span>
                  <span className="relative z-10 hidden sm:inline ml-1">{t.questEdit.delete}</span>
                </button>
                <button
                  onClick={handleSave}
                  className="bg-gradient-to-b from-blue-800/60 via-blue-900/50 to-blue-950/60 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/50 border-r-2 border-b-2 border-r-blue-700/70 border-b-blue-700/70 rounded-xl text-white h-10 flex-1 sm:flex-none sm:w-auto sm:h-auto sm:py-2 font-orbitron font-bold text-xs tracking-wide uppercase transition-all hover:scale-105 relative flex items-center justify-center"
                  style={{
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                  }}
                  title={t.questEdit.saveChanges}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                  <span className="relative z-10 text-base">💾</span>
                  <span className="relative z-10 hidden sm:inline ml-1">{t.questEdit.saveChanges}</span>
                </button>
              </div>
            ) : (
              // Original non-compact footer
              (isEditing ? (
                <div className="flex gap-2.5 sm:gap-3">
                  <button
                    onClick={handleCancel}
                    className="flex-1 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-xl text-white py-2 font-orbitron font-bold text-xs tracking-wide uppercase transition-all hover:scale-105 relative"
                    style={{
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-black/10 rounded-2xl"></div>
                    <span className="relative z-10">{t.questEdit.cancel}</span>
                  </button>
                  <button
                    onClick={handleSave}
                    className="flex-1 bg-gradient-to-b from-blue-800/60 via-blue-900/50 to-blue-950/60 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/50 border-r-2 border-b-2 border-r-blue-700/70 border-b-blue-700/70 rounded-xl text-white py-2 font-orbitron font-bold text-xs tracking-wide uppercase transition-all hover:scale-105 relative"
                    style={{
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                    <span className="relative z-10">{t.questEdit.saveChanges}</span>
                  </button>
                </div>
              ) : (
                <div className="flex gap-2.5 sm:grid sm:grid-cols-3 sm:gap-3">
                  {quest.status !== 'completed' && (
                    <button
                      onClick={() => {
                        onComplete(quest.id);
                        onClose();
                      }}
                      className="bg-gradient-to-b from-green-800/60 via-green-900/50 to-green-950/60 backdrop-blur-sm border-t-2 border-l-2 border-green-500/50 border-r-2 border-b-2 border-r-green-700/70 border-b-green-700/70 rounded-xl text-white h-10 flex-1 sm:flex-none sm:w-auto sm:h-auto sm:py-2 font-orbitron font-bold text-xs tracking-wide uppercase transition-all hover:scale-105 relative flex items-center justify-center"
                      style={{
                        boxShadow: '0 4px 12px rgba(34, 197, 94, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                      }}
                      title={t.questEdit.complete}
                    >
                      <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                      <span className="relative z-10 text-base">✓</span>
                      <span className="relative z-10 hidden sm:inline ml-1">{t.questEdit.complete}</span>
                    </button>
                  )}
                  {!calendarCompact && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="bg-gradient-to-b from-blue-800/60 via-blue-900/50 to-blue-950/60 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/50 border-r-2 border-b-2 border-r-blue-700/70 border-b-blue-700/70 rounded-xl text-white h-10 flex-1 sm:flex-none sm:w-auto sm:h-auto sm:py-2 font-orbitron font-bold text-xs tracking-wide uppercase transition-all hover:scale-105 relative flex items-center justify-center"
                      style={{
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                      }}
                      title={t.questEdit.edit}
                    >
                      <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                      <span className="relative z-10 text-base">✏️</span>
                      <span className="relative z-10 hidden sm:inline ml-1">{t.questEdit.edit}</span>
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (window.confirm(t.questEdit.confirmDelete)) {
                        onDelete(quest.id);
                        onClose();
                      }
                    }}
                    className="bg-gradient-to-b from-red-800/60 via-red-900/50 to-red-950/60 backdrop-blur-sm border-t-2 border-l-2 border-red-500/50 border-r-2 border-b-2 border-r-red-700/70 border-b-red-700/70 rounded-xl text-white h-10 flex-1 sm:flex-none sm:w-auto sm:h-auto sm:py-2 font-orbitron font-bold text-xs tracking-wide uppercase transition-all hover:scale-105 relative flex items-center justify-center"
                    style={{
                      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                    }}
                    title={t.questEdit.delete}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                    <span className="relative z-10 text-base">🗑️</span>
                    <span className="relative z-10 hidden sm:inline ml-1">{t.questEdit.delete}</span>
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
    </div>,
    document.body
  );
};

export default QuestEditModal;

